import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LuLogOut, LuRefreshCw } from "react-icons/lu";
import "./App.css";

const STATUS_TOKEN_KEY = "status_token";

const SERVICE_TYPE_LABELS = {
  https: "HTTPS",
  mongodb: "MONGODB",
  influxdb: "INFLUXDB",
  submission: "SUBMISSION",
  smtps: "SMTPS",
  imaps: "IMAPS",
};
const SERVICE_IDS = [
  // --- Apps clients en production ---
  "enersol-admin-web",
  "enersol-admin-api",
  "eurochapefluide-admin-web",
  "eurochapefluide-api",
  "ksalesconsulting-web",
  // --- Produits Ygalaad en production ---
  "ygalaad-website",
  "passlink-web",
  "passlink-api",
  "passgen-web",
  "passsign-app-prod",
  "passsign-api-prod",
  "passsign-api-dev",
  "passsign-app-dev",
  "mastersudokuchallenge-store",
  "mastersudokuchallenge-api",
  "chapeo-web",
  "chapeo-api",
  "ticketbox-api-prod",
  "ticketbox-api-dev",
  "ticketbox-app-prod",
  "ticketbox-app-dev",
  "ticketbox-dashboard-prod",
  "ticketbox-dashboard-dev",
  "ticketbox-store",
  "pandagpt-api",
  // --- Outils internes / infrastructure ---
  "crm-web",
  "crm-api",
  "sparkle-api",
  "sparkle-mongodb",
  "nextcloud-web",
  "grafana-web",
  "influxdb-core",
  "ygalaad-gitlab",
  "ygalaad-mail-submission",
  "ygalaad-mail-imaps"
];
const PLACEHOLDER_SERVICES = SERVICE_IDS.map((id) => ({ id }));

const getHttpClassName = (statusCode) => {
  if (!statusCode) return "metric-neutral";
  if (statusCode >= 200 && statusCode < 300) return "metric-good";
  if (statusCode >= 300 && statusCode < 500) return "metric-warn";
  return "metric-bad";
};

const getLatencyClassName = (latencyMs) => {
  if (latencyMs == null) return "metric-neutral";
  if (latencyMs < 300) return "metric-good";
  if (latencyMs < 1000) return "metric-warn";
  return "metric-bad";
};

const HEALTH_TIMEOUT_MS = 4000;
const HEALTH_POLL_MS = 5000;
// Un ping /health manqué isolé (perte réseau ponctuelle) ne suffit pas à
// déclarer l'API de monitoring injoignable.
const HEALTH_FAILURES_BEFORE_FATAL = 2;

const MONITORING_API_FATAL_MESSAGE =
  "CRITIQUE — L'API de monitoring est injoignable. Les indicateurs ci-dessous peuvent être obsolètes ou incomplets.";

function App() {
  const apiBaseUrl = useMemo(
    () => process.env.REACT_APP_STATUS_API_URL || "http://localhost:8000",
    [],
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [token, setToken] = useState(
    () => localStorage.getItem(STATUS_TOKEN_KEY) || "",
  );
  const latestRequestRef = useRef(0);
  const statusAbortRef = useRef(null);
  const refetchOnRecoveryRef = useRef(false);
  const [state, setState] = useState(() => ({
    loading: !!localStorage.getItem(STATUS_TOKEN_KEY),
    error: "",
    data: localStorage.getItem(STATUS_TOKEN_KEY)
      ? {
          status: "operational",
          version: "1.0.0",
          timestamp: new Date().toISOString(),
          checked_at: new Date().toISOString(),
          services: PLACEHOLDER_SERVICES,
        }
      : null,
  }));
  const [loadingServiceIds, setLoadingServiceIds] = useState(() =>
    localStorage.getItem(STATUS_TOKEN_KEY) ? [...SERVICE_IDS] : [],
  );
  const [monitoringApiFatal, setMonitoringApiFatal] = useState("");
  const [loginHealthFailed, setLoginHealthFailed] = useState(false);
  const [selectedTag, setSelectedTag] = useState("all");
  const [selectedHealth, setSelectedHealth] = useState("all");
  const [now, setNow] = useState(() => new Date());
  const friendlyNow = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).format(now),
    [now],
  );

  const pingMonitoringApi = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      HEALTH_TIMEOUT_MS,
    );
    try {
      const response = await fetch(`${apiBaseUrl}/health`, {
        method: "GET",
        signal: controller.signal,
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }, [apiBaseUrl]);

  const fetchStatus = useCallback(async () => {
    if (!token) return;
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    // Annule la vérification précédente encore en vol plutôt que d'empiler
    // les requêtes côté navigateur.
    statusAbortRef.current?.abort();
    const controller = new AbortController();
    statusAbortRef.current = controller;

    try {
      const apiAlive = await pingMonitoringApi();
      if (latestRequestRef.current !== requestId) return;
      if (!apiAlive) {
        // Le bandeau critique est géré par le polling /health ; on relancera
        // la vérification dès que l'API répondra de nouveau.
        refetchOnRecoveryRef.current = true;
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }
      refetchOnRecoveryRef.current = false;

      const nowIso = new Date().toISOString();
      setLoadingServiceIds([...SERVICE_IDS]);
      setState((prev) => ({
        loading: true,
        error: "",
        data: {
          status: prev.data?.status || "operational",
          version: prev.data?.version || "1.0.0",
          timestamp: prev.data?.timestamp || nowIso,
          checked_at: prev.data?.checked_at || nowIso,
          services:
            prev.data?.services && prev.data.services.length > 0
              ? prev.data.services
              : PLACEHOLDER_SERVICES,
        },
      }));
      // Un seul appel /status pour tous les services (l'API les vérifie en
      // parallèle) : en HTTP/1.1 le navigateur limite à 6 connexions par
      // origine, et une requête par service saturait ces connexions avec des
      // checks lents, bloquant le ping /health jusqu'à son timeout.
      const response = await fetch(`${apiBaseUrl}/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
      });

      if (response.status === 401) {
        localStorage.removeItem(STATUS_TOKEN_KEY);
        setToken("");
        throw new Error("Session expired");
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      if (latestRequestRef.current !== requestId) return;
      setState({
        loading: false,
        error: "",
        data: {
          status: payload.status || "operational",
          version: payload.version || "1.0.0",
          timestamp:
            payload.timestamp || payload.checked_at || new Date().toISOString(),
          checked_at:
            payload.checked_at || payload.timestamp || new Date().toISOString(),
          services: payload.services || [],
        },
      });
    } catch (error) {
      if (latestRequestRef.current !== requestId) return;
      if (error.name === "AbortError") return;
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || "Unknown error",
      }));
    } finally {
      if (statusAbortRef.current === controller) {
        statusAbortRef.current = null;
      }
      if (latestRequestRef.current === requestId) {
        setLoadingServiceIds([]);
      }
    }
  }, [apiBaseUrl, token, pingMonitoringApi]);

  useEffect(() => {
    let cancelled = false;
    let consecutiveFailures = 0;

    const tick = async () => {
      const ok = await pingMonitoringApi();
      if (cancelled) return;

      if (!token) {
        setLoginHealthFailed(!ok);
        return;
      }

      if (!ok) {
        consecutiveFailures += 1;
        if (consecutiveFailures < HEALTH_FAILURES_BEFORE_FATAL) return;
        refetchOnRecoveryRef.current = true;
        setMonitoringApiFatal(MONITORING_API_FATAL_MESSAGE);
        setLoadingServiceIds([]);
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }

      consecutiveFailures = 0;
      setMonitoringApiFatal("");
      if (refetchOnRecoveryRef.current) {
        refetchOnRecoveryRef.current = false;
        fetchStatus();
      }
    };

    tick();
    const intervalId = window.setInterval(tick, HEALTH_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [token, pingMonitoringApi, fetchStatus]);

  useEffect(() => {
    if (!token) {
      setMonitoringApiFatal("");
      setState((prev) =>
        prev.loading || prev.error || prev.data
          ? { loading: false, error: "", data: null }
          : prev,
      );
      setLoadingServiceIds((prev) => (prev.length ? [] : prev));
      return;
    }
    fetchStatus();
    // Quand le réseau est dégradé, une vérification (timeouts + relance côté
    // API) peut dépasser la minute : on la laisse aboutir au lieu de
    // l'annuler, sinon aucun résultat ne s'afficherait pendant l'incident.
    const intervalId = setInterval(() => {
      if (!statusAbortRef.current) fetchStatus();
    }, 60 * 1000);
    return () => {
      clearInterval(intervalId);
      statusAbortRef.current?.abort();
    };
  }, [fetchStatus, token]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setAuthError("");

    try {
      const response = await fetch(`${apiBaseUrl}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        throw new Error("Invalid credentials");
      }

      const payload = await response.json();
      localStorage.setItem(STATUS_TOKEN_KEY, payload.access_token);
      setToken(payload.access_token);
      setUsername("");
      setPassword("");
    } catch (error) {
      setAuthError(error.message || "Login failed");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STATUS_TOKEN_KEY);
    setToken("");
  };

  const servicesById = useMemo(() => {
    const list = state.data?.services || [];
    return list.reduce((acc, service) => {
      acc[service.id] = service;
      return acc;
    }, {});
  }, [state.data?.services]);
  const renderedServices = SERVICE_IDS.map(
    (serviceId) => servicesById[serviceId] || { id: serviceId },
  );
  const availableTags = useMemo(() => {
    const tagsSet = new Set();
    renderedServices.forEach((service) => {
      if (!Array.isArray(service.tags)) return;
      service.tags.forEach((tag) => {
        if (typeof tag === "string" && tag.trim()) {
          tagsSet.add(tag.trim());
        }
      });
    });
    return Array.from(tagsSet).sort((a, b) => a.localeCompare(b));
  }, [renderedServices]);
  const filteredServices = useMemo(() => {
    return renderedServices.filter((service) => {
      const tagMatch =
        selectedTag === "all" ||
        (Array.isArray(service.tags) && service.tags.includes(selectedTag));
      const healthMatch =
        selectedHealth === "all" ||
        (selectedHealth === "up" && service.up === true) ||
        (selectedHealth === "down" && service.up === false);
      return tagMatch && healthMatch;
    });
  }, [renderedServices, selectedTag, selectedHealth]);
  const checkedServices = renderedServices.filter(
    (service) => typeof service.up === "boolean",
  );
  const upCount = checkedServices.filter((service) => service.up).length;
  const downCount = checkedServices.filter((service) => !service.up).length;

  if (!token) {
    return (
      <main className="status-page">
        <p className="login-api-url">
          API URL: <code>{apiBaseUrl}</code>
        </p>
        <form className="login-form" onSubmit={handleLogin}>
          <input
            className="login-input"
            type="text"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="Username"
            autoComplete="username"
            required
          />
          <input
            className="login-input"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Password"
            autoComplete="current-password"
            required
          />
          <button className="login-button" type="submit">
            Login
          </button>
        </form>
        {authError ? <p className="status-error">{authError}</p> : null}
        {loginHealthFailed ? (
          <p className="monitoring-health-login-warn">
            Impossible de joindre l&apos;API de monitoring (endpoint /health).
            Vérifiez l&apos;URL et le réseau.
          </p>
        ) : null}
      </main>
    );
  }

  return (
    <main className="status-page">
      {monitoringApiFatal ? (
        <div className="monitoring-api-catastrophic" role="alert">
          {monitoringApiFatal}
        </div>
      ) : null}
      <header>
        <div className="header-left">
          <span className="header-clock">{friendlyNow}</span>
        </div>
        <div className="header-center">
          <div className="status-summary">
            <button
              className={`status-summary-button status-summary-up ${selectedHealth === "up" ? "active" : ""}`}
              type="button"
              onClick={() =>
                setSelectedHealth((prev) => (prev === "up" ? "all" : "up"))
              }
            >
              UP: {upCount}
            </button>
            <button
              className={`status-summary-button status-summary-down ${selectedHealth === "down" ? "active" : ""}`}
              type="button"
              onClick={() =>
                setSelectedHealth((prev) => (prev === "down" ? "all" : "down"))
              }
            >
              DOWN: {downCount}
            </button>
          </div>
        </div>
        <div className="header-right">
          <div className="top-actions">
            <button
              className="icon-button"
              type="button"
              onClick={fetchStatus}
              title="Refresh"
              aria-label="Refresh"
            >
              <LuRefreshCw />
            </button>
            <button
              className="icon-button"
              type="button"
              onClick={handleLogout}
              title="Logout"
              aria-label="Logout"
            >
              <LuLogOut />
            </button>
          </div>
        </div>
      </header>
      {availableTags.length > 0 ? (
        <section className="tags-filter" aria-label="Filtre par tag">
          <button
            className={`tag-filter-button ${selectedTag === "all" ? "active" : ""}`}
            type="button"
            onClick={() => setSelectedTag("all")}
          >
            Tous
          </button>
          {availableTags.map((tag) => (
            <button
              className={`tag-filter-button ${selectedTag === tag ? "active" : ""}`}
              type="button"
              key={tag}
              onClick={() => setSelectedTag(tag)}
            >
              {tag}
            </button>
          ))}
        </section>
      ) : null}
      <section className="services-grid">
        {state.error ? (
          <article className="service-card status-error">
            Error: {state.error}
          </article>
        ) : null}
        {filteredServices.map((service) => (
          <article
            className={`service-card service-card-${service.type || "https"} ${
              typeof service.up === "boolean"
                ? service.up
                  ? "service-card-up"
                  : "service-card-down"
                : ""
            }`}
            key={service.id}
          >
            <div className="service-card-header">
              <p
                className={`service-name ${service.up === false ? "service-name-down" : ""}`}
              >
                {service.id}
              </p>
              <div className="service-type-wrap">
                <p className="service-type">
                  {SERVICE_TYPE_LABELS[service.type] ||
                    service.type ||
                    "UNKNOWN"}
                </p>
                {loadingServiceIds.includes(service.id) ? (
                  <div className="inline-spinner" />
                ) : null}
              </div>
            </div>
            <p
              className={
                typeof service.up === "boolean"
                  ? service.up
                    ? "service-status status-ok"
                    : "service-status status-error service-status-down"
                  : "service-status"
              }
            >
              {typeof service.up === "boolean"
                ? service.up
                  ? "UP"
                  : "DOWN"
                : "CHECKING..."}
            </p>
            {typeof service.url === "string" &&
            /^https?:\/\//i.test(service.url) ? (
              <a
                className="service-url"
                href={service.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                {service.url}
              </a>
            ) : (
              <p className="service-url">{service.url || service.id}</p>
            )}
            <div className="service-metrics">
              {typeof service.up !== "boolean" ? (
                <p className="status-meta metric-neutral">
                  Waiting for check...
                </p>
              ) : service.type === "https" ? (
                <p
                  className={`status-meta ${getHttpClassName(service.status_code)}`}
                >
                  HTTPS: {service.status_code ?? "N/A"}
                </p>
              ) : service.type === "submission" ? (
                <p
                  className={`status-meta ${service.up ? "metric-good" : "metric-bad"}`}
                >
                  SUBMISSION: {service.up ? "OPEN" : "CLOSED"}
                </p>
              ) : service.type === "smtps" ? (
                <p
                  className={`status-meta ${service.up ? "metric-good" : "metric-bad"}`}
                >
                  SMTPS: {service.up ? "OPEN" : "CLOSED"}
                </p>
              ) : service.type === "imaps" ? (
                <p
                  className={`status-meta ${service.up ? "metric-good" : "metric-bad"}`}
                >
                  IMAPS: {service.up ? "OPEN" : "CLOSED"}
                </p>
              ) : service.type === "influxdb" ? (
                <p
                  className={`status-meta ${service.up ? "metric-good" : "metric-bad"}`}
                >
                  INFLUXDB: {service.up ? "OPEN" : "CLOSED"}
                </p>
              ) : service.type === "mongodb" ? (
                <p
                  className={`status-meta ${service.up ? "metric-good" : "metric-bad"}`}
                >
                  MONGODB: {service.up ? "CONNECTED" : "FAILED"}
                </p>
              ) : (
                <p
                  className={`status-meta ${service.up ? "metric-good" : "metric-bad"}`}
                >
                  TCP: {service.up ? "OPEN" : "CLOSED"}
                </p>
              )}
              <p
                className={`status-meta ${getLatencyClassName(service.response_time_ms)}`}
              >
                Latency: {service.response_time_ms} ms
              </p>
            </div>
            {service.error ? (
              <p className="status-error service-error">{service.error}</p>
            ) : null}
            {Array.isArray(service.tags) && service.tags.length > 0 ? (
              <div className="service-tags" aria-label="Service tags">
                {service.tags.map((tag) => (
                  <span className="service-tag" key={`${service.id}-${tag}`}>
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
          </article>
        ))}
      </section>
      {state.data?.timestamp || state.data?.checked_at ? (
        <p className="last-call-timestamp">
          {new Date(
            state.data?.timestamp || state.data?.checked_at,
          ).toLocaleString()}
        </p>
      ) : null}
    </main>
  );
}

export default App;
